import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  Paper,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  AttachFile,
  Close,
  CheckCircle,
  Delete,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/setup/redux/RootReducer';
import { uploadOrderDocumentAsync, fetchDocumentTypes } from '../redux/DocumentRedux';
import { LabeledField } from '@/__core/components/LabeledField';
import { AddButtonSelect } from '@/__core/hocs';
import DocumentTypeManager from './DocumentTypeManager';

interface FileUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  orderId?: string;
}

interface FileUploadState {
  files: File[];
  documentTypeId: string;
  uploading: boolean;
  error: string | null;
  success: boolean;
}

interface DocumentType {
  id: string;
  name: string;
  description?: string;
  color?: string;
  sort_order?: number;
  created_at?: string;
  updated_at?: string;
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({
  open,
  onClose,
  onSuccess,
  orderId
}) => {
  const dispatch = useDispatch();
  const { documentTypes, loading: documentTypesLoading } = useSelector((state: RootState) => state.documents);
  const authState = useSelector((state: RootState) => state.auth);
  
  const [showDocumentTypeManager, setShowDocumentTypeManager] = useState(false);
  const [newlyCreatedDocumentTypeId, setNewlyCreatedDocumentTypeId] = useState<string | null>(null);
  
  const [uploadState, setUploadState] = useState<FileUploadState>({
    files: [],
    documentTypeId: '',
    uploading: false,
    error: null,
    success: false
  });
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-select the first document type when documentTypes are loaded
  useEffect(() => {
    if (documentTypes.length > 0 && !uploadState.documentTypeId && !documentTypesLoading) {
      setUploadState(prev => ({
        ...prev,
        documentTypeId: documentTypes[0].id
      }));
    }
  }, [documentTypes, documentTypesLoading]);

  // Debug: Log when documentTypeId changes
  useEffect(() => {
  }, [uploadState.documentTypeId, documentTypes]);

  // Ensure newly created document type is selected when it becomes available
  useEffect(() => {
    if (newlyCreatedDocumentTypeId && documentTypes.length > 0) {
      const newlyCreatedType = documentTypes.find(type => type.id === newlyCreatedDocumentTypeId);
      if (newlyCreatedType && uploadState.documentTypeId !== newlyCreatedDocumentTypeId) {
        setUploadState(prev => ({
          ...prev,
          documentTypeId: newlyCreatedDocumentTypeId
        }));
        setNewlyCreatedDocumentTypeId(null); // Reset the flag
      }
    }
  }, [documentTypes, newlyCreatedDocumentTypeId, uploadState.documentTypeId]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > 0) {
      setUploadState(prev => ({
        ...prev,
        files: [...prev.files, ...selectedFiles],
        error: null,
        success: false
      }));
    }
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadState(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const handleDocumentTypeChange = (event: any) => {
    setUploadState(prev => ({
      ...prev,
      documentTypeId: event.target.value,
      error: null
    }));
  };

  const handleDocumentTypeCreated = (newDocumentType: DocumentType) => {
    // Refresh document types after creating a new one
    dispatch(fetchDocumentTypes());
    // Auto-select the newly created document type
    setUploadState(prev => ({
      ...prev,
      documentTypeId: newDocumentType.id,
      error: null
    }));
  };

  const handleUpload = async () => {
    if (uploadState.files.length === 0 || !uploadState.documentTypeId || !orderId || !authState.accessToken) {
      setUploadState(prev => ({
        ...prev,
        error: 'Vui lòng chọn ít nhất một file và loại tài liệu'
      }));
      return;
    }

    setUploadState(prev => ({
      ...prev,
      uploading: true,
      error: null
    }));

    try {
      for (const file of uploadState.files) {
        const result = await dispatch(uploadOrderDocumentAsync({
          orderId,
          file,
          documentTypeId: uploadState.documentTypeId
        }));

        if (!uploadOrderDocumentAsync.fulfilled.match(result)) {
          throw new Error(`Upload file ${file.name} thất bại`);
        }
      }

      setUploadState(prev => ({
        ...prev,
        uploading: false,
        success: true
      }));

      setTimeout(() => {
        setUploadState({
          files: [],
          documentTypeId: '',
          uploading: false,
          error: null,
          success: false
        });
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (error: any) {
      let errorMessage = 'Có lỗi xảy ra khi tải lên file';

      if (error.response?.data?.content_type) {
        errorMessage = 'Lỗi: Content type không được tìm thấy. Vui lòng thử lại.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setUploadState(prev => ({
        ...prev,
        uploading: false,
        error: errorMessage
      }));
    }
  };

  const handleClose = () => {
    if (!uploadState.uploading) {
      setUploadState({
        files: [],
        documentTypeId: '',
        uploading: false,
        error: null,
        success: false
      });
      onClose();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={uploadState.uploading}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CloudUpload sx={{ color: 'primary.main' }} />
            <Typography variant="h6">Tải lên tài liệu</Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={uploadState.uploading}
            size="small"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {uploadState.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {uploadState.error}
          </Alert>
        )}
        
        {uploadState.success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle />
              <Typography>Tải lên thành công!</Typography>
            </Box>
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          {/* File Selection */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Chọn file
            </Typography>
            <Paper
              sx={{
                p: 3,
                border: '2px dashed',
                borderColor: uploadState.files.length > 0 ? 'primary.main' : 'divider',
                borderRadius: 2,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                bgcolor: uploadState.files.length > 0 ? 'primary.light' : 'background.paper',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.light'
                }
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <CloudUpload sx={{ fontSize: 48, color: uploadState.files.length > 0 ? 'primary.main' : 'text.secondary', mb: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Nhấp để chọn file
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {uploadState.files.length > 0 ? `Đã chọn ${uploadState.files.length} file` : 'Hoặc kéo thả file vào đây'}
              </Typography>
            </Paper>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              multiple
              accept=".txt,.png,.jpg,.jpeg,.pdf,.doc,.docx,.zip,.rar,.mp4,.mp3,.avi,.mov,.gif,.bmp,.tiff,.svg,.webp"
            />

            {uploadState.files.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {uploadState.files.map((file, index) => (
                  <Paper
                    key={`${file.name}-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      backgroundColor: '#f9fafb',
                    }}
                  >
                    <AttachFile sx={{ fontSize: 18, color: '#6b7280', flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(file.size)}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(index);
                      }}
                      disabled={uploadState.uploading}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>

          {/* Document Type Selection */}
          <LabeledField label="Loại tài liệu" labelWidth={120}>
            <AddButtonSelect
              options={documentTypes}
              value={uploadState.documentTypeId}
              onChange={handleDocumentTypeChange}
              placeholder="Chọn loại tài liệu"
              size="small"
              disabled={uploadState.uploading || documentTypesLoading}
              CustomDialog={({ open, onClose, onSave, loading }) => (
                <DocumentTypeManager
                  open={open}
                  onClose={onClose}
                  onSuccess={(documentType) => {
                    
                    // Set the newly created document type ID
                    setNewlyCreatedDocumentTypeId(documentType.id);
                    
                    // Auto-select the newly created document type
                    setUploadState(prev => ({
                      ...prev,
                      documentTypeId: documentType.id,
                      error: null
                    }));
                    
                    // Refresh document types list
                    dispatch(fetchDocumentTypes());
                    
                    // Call the original onSave callback
                    onSave?.(documentType);
                  }}
                />
              )}
              onAddNewCustom={async (documentTypeData: any) => {
                // Return the document type data as received from API
                return documentTypeData;
              }}
            />
          </LabeledField>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button
          onClick={handleClose}
          disabled={uploadState.uploading}
        >
          Hủy
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={uploadState.files.length === 0 || !uploadState.documentTypeId || uploadState.uploading}
          startIcon={uploadState.uploading ? <CircularProgress size={16} /> : <CloudUpload />}
        >
          {uploadState.uploading ? 'Đang tải lên...' : 'Tải lên'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileUploadModal;
